import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';

import Card from 'components/Card';
import Spinner from 'components/Spinner';
import PriceMarketChart from 'modules/Prices/pages/price/components/GraphByMarkets/chart';
import PriceMarketHeader from 'modules/Prices/pages/price/components/GraphByMarkets/header';
import PriceSelectCompetitor from 'modules/Prices/pages/price/components/SelectCompetitor';
import PriceChartDiv from 'modules/Prices/components/StyledDiv/graph';

import { components } from 'apiTyped/types';
import { RivalsByVintage } from 'interface/price';
import { dataFilter } from 'modules/Prices/helpers';
import { getDataFilters } from 'modules/Prices/pages/price/components/GraphByMarkets/helpers';

const PriceDiv = styled.div`
  position: relative;
  display: grid;
`;

const PriceMarket = ({
  data,
  vintagesByWine, // Should be indexed by wine_id later on
  vintage,
  format,
  wine,
  showVintages,
  exportName,
  isLoading,
  competitor,
  competitorVintage,
  setCompetitor,
  setCompetitorVintage
}: {
  data: components['schemas']['DataDetail'][];
  // eslint-disable-next-line no-unused-vars
  vintagesByWine: RivalsByVintage;
  vintage: string | null;
  format: string | null;
  wine: string | null;
  showVintages: boolean;
  exportName: string | null;
  isLoading: boolean;
  competitor: string | null;
  competitorVintage?: string | null;
  // eslint-disable-next-line no-unused-vars
  setCompetitor: (e: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  setCompetitorVintage?: (e: string | null) => void;
}) => {
  const [network, setNetwork] = useState<string | null>(null);
  const [markets, setMarkets] = useState<string[]>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  const [activeRadioButton, setActiveRadioButton] =
    useState<string>('Aucune comparaison');

  const headerOptions = {
    markets: [...new Set(data.map((item) => item.market_name))],
    networks: [...new Set(data.map((item) => item.network_code))]
  };

  const getDefaultMarketOptions = ({
    data,
    wine,
    vintage,
    format,
    network
  }: {
    data: components['schemas']['DataDetail'][];
    wine: string | null;
    vintage: string | null;
    format: string | null;
    network: string | null;
  }) =>
    [
      ...new Set(
        dataFilter(
          data,
          getDataFilters({ vintage, wine, format, network })
        ).map((item) => item.market_name)
      )
    ].slice(0, 7);

  const onChangeCompetitor = (competitor: string | null) => {
    setCompetitor(competitor);
    setActiveRadioButton(
      competitor !== null ? 'Comparer avec un concurrent' : 'Aucune comparaison'
    );
  };

  // Set network, markets and currency when data changes
  useEffect(() => {
    if (data.length > 0) {
      const network = get(data, '[0].network_code', null);
      setNetwork(network);

      const currency = get(data, '[0].displayed_currency', 'EUR');
      setCurrency(currency);
    }
  }, [data, vintage, format, wine]);

  // update defaultMarkets and activeButton when network change
  useEffect(() => {
    const defaultMarkets = network
      ? getDefaultMarketOptions({ data, wine, vintage, format, network })
      : [];
    setMarkets(defaultMarkets);

    if (network === 'WEB') {
      setActiveRadioButton('Aucune comparaison');
    }
  }, [network]);

  return (
    <PriceChartDiv>
      <PriceDiv>
        <Card id="prices-price">
          <PriceMarketHeader
            exportName={exportName}
            network={network}
            markets={markets}
            handleNetwork={setNetwork}
            setMarkets={setMarkets}
            options={headerOptions}
          />
          <PriceMarketChart
            wine={wine}
            vintage={vintage}
            format={format}
            network={network}
            markets={markets}
            currency={currency}
            competitor={competitor}
            competitorVintage={competitorVintage}
            activeRadioButton={activeRadioButton}
            data={data}
          />
          <PriceSelectCompetitor
            vintagesByWine={vintagesByWine}
            competitor={competitor}
            competitorVintage={competitorVintage}
            onChangeCompetitor={onChangeCompetitor}
            onChangeCompetitorVintage={setCompetitorVintage}
            showVintages={showVintages}
            vintage={vintage}
          />
        </Card>
        <Spinner loading={isLoading} little />
      </PriceDiv>
    </PriceChartDiv>
  );
};

export default PriceMarket;
